// The replacement's delivery date, staged with its step (2026-11-02, Jenn;
// docs/pipeline-behaviors.md "Delivery estimates";
// docs/decisions/2026-11-02-replacement-edd-at-staging.md).
//
// A process_replacement step is estimated from its own values (the items and
// quantities selected, the ship-to ZIP, the shipping method, the order's
// brand) when the suggestion is generated, and again when the person
// changes those values. The record lives on the step (step.deliveryEstimate):
//
//   { source: 'staged_replacement', estimatedAt, sourceLabel,
//     deliveryDate, shipDate, optionName, method,
//     noDate?: { kind, text },        when there is no date
//     input: { brand, zipCode, items, method } }
//   or { awaitingItems: true }        no item selected yet (Other Actions)
//
// Pure helpers shared by the server (pipeline, executor, route) and the
// agent app (the step's line, the re-estimate, the reply update). No calls.

export const REPLACEMENT_EDD_SOURCE = 'staged_replacement'

// Why there is no date, for the report and the warning.
export const REPLACEMENT_EDD_NO_DATE_KINDS = Object.freeze([
  'out_of_stock', 'dropship', 'no_option', 'option_missing', 'call_failed', 'no_zip', 'no_brand', 'no_items', 'no_text',
])

const normMethod = (m) => String(m ?? '').trim().replace(/\s+/g, ' ').toUpperCase()
const zip5 = (raw) => {
  const s = String(raw ?? '').trim()
  const us = /^(\d{5})(?:-?\d{4})?$/.exec(s)
  return us ? us[1] : s
}

// The step's values as the estimate takes them. selection: the person's
// current inputs ({ items, shippingMethod, address }), else the step's own
// defaults (itemSelection.defaultSelected, shippingSelection).
export function replacementEddValues(step, selection = null) {
  const sel = selection && typeof selection === 'object' ? selection : null
  const rawItems = Array.isArray(sel?.items) ? sel.items : (step?.itemSelection?.defaultSelected ?? [])
  const items = (Array.isArray(rawItems) ? rawItems : [])
    .map((i) => ({ sku: String(i?.sku ?? '').trim(), quantity: Math.max(1, Math.trunc(Number(i?.quantity)) || 1) }))
    .filter((i) => i.sku)
  const shippingMethod = (sel && 'shippingMethod' in sel ? sel.shippingMethod : null) ?? step?.shippingSelection?.defaultMethod ?? null
  const addr = sel?.address ?? step?.shippingSelection?.address ?? null
  const rawZip = addr?.shippingPostalCode ?? null
  return { items, shippingMethod: shippingMethod || null, zipCode: rawZip ? zip5(rawZip) || null : null }
}

// One key for "the same request": the items (sorted, SKU in upper case,
// quantities summed), the method and the 5-digit ZIP.
export function replacementEddKey({ items = [], shippingMethod = null, method = null, zipCode = null } = {}) {
  const sum = new Map()
  for (const i of items ?? []) {
    const sku = String(i?.sku ?? '').trim().toUpperCase()
    if (!sku) continue
    sum.set(sku, (sum.get(sku) ?? 0) + (Math.max(1, Math.trunc(Number(i?.quantity)) || 1)))
  }
  const list = [...sum.entries()].sort(([a], [b]) => (a < b ? -1 : a > b ? 1 : 0)).map(([s, q]) => `${s}x${q}`).join(',')
  return `${list}|${normMethod(shippingMethod ?? method)}|${zip5(zipCode)}`
}

// Whether a staged record was made for these values.
export function stagedEddMatches(record, values) {
  if (!record?.input) return false
  return replacementEddKey(record.input) === replacementEddKey(values ?? {})
}

// Whether a record carries a usable estimate (a date, or the no-date reason).
export function isStagedEddRecord(record) {
  return Boolean(record && typeof record === 'object' && record.source === REPLACEMENT_EDD_SOURCE && record.estimatedAt)
}

// "10:42 AM ET".
export function etClockLabel(iso) {
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return null
  return `${new Intl.DateTimeFormat('en-US', { timeZone: 'America/New_York', hour: 'numeric', minute: '2-digit', hour12: true }).format(d)} ET`
}

// The step's line a CSR sees: "Estimated delivery 11/06/2026", the date
// only (Jenn, 2026-11-02: no method, no estimate time), or null when there
// is no date.
export function stagedEddLabel(record) {
  if (!isStagedEddRecord(record) || !record.deliveryDate || record.outOfStock) return null
  return `Estimated delivery ${record.deliveryDate}`
}

// The Activity Log's fuller line: "Estimated delivery 11/06/2026 (Standard,
// storefront estimate at 10:42 AM ET)". The estimate time stays internal.
export function stagedEddLogLabel(record) {
  if (!isStagedEddRecord(record) || !record.deliveryDate) return null
  const at = etClockLabel(record.estimatedAt)
  return `Estimated delivery ${record.deliveryDate} (${record.optionName ?? record.method ?? 'Standard'}, storefront estimate${at ? ` at ${at}` : ''})`
}

// The step's warning when there is no date, or null.
export function stagedEddWarning(record) {
  if (!isStagedEddRecord(record) || stagedQuote(record)) return null
  // Out of stock: the step's out-of-stock line says it (one red line).
  if (record.outOfStock || record.sentenceKind === 'out_of_stock') return null
  return record.noDate?.text ?? 'No delivery date from the storefront.'
}

// What the reply quotes from a record (2026-11-03): the date (MM/DD/YYYY),
// else the chosen option's own display sentence (dropship, out of stock,
// the fallback); null when the storefront gave nothing (INSERTEDD).
// A record from before deliveryText existed quotes its date.
export function stagedQuote(record) {
  if (!record || typeof record !== 'object') return null
  return record.deliveryText ?? record.deliveryDate ?? null
}

// The step's line when the reply got the storefront's message, not a date
// (Jenn, 2026-11-03): exactly what was put in the reply, and the Ship Date
// text when that is a message too. null for a date.
export function stagedEddSentenceLine(record) {
  if (!isStagedEddRecord(record) || record.deliveryDate || !stagedQuote(record)) return null
  // Out of stock: only the out-of-stock line shows (2026-11-03).
  if (record.outOfStock || record.sentenceKind === 'out_of_stock') return null
  const ship = record.shipText && record.shipText !== record.shipDate && !/^\d{2}\/\d{2}\/\d{4}$/.test(record.shipText) ? ` Ship Date: "${record.shipText}"` : ''
  return `Inserted in the reply: "${stagedQuote(record)}"${ship}`
}

// The Activity Log line at Process: "Delivery date kept from the staged
// estimate (11/06/2026, estimated at 10:42 AM ET)".
export function stagedEddKeptLine(record) {
  if (!isStagedEddRecord(record)) return null
  const at = etClockLabel(record.estimatedAt)
  if (!record.deliveryDate && stagedQuote(record)) return `Delivery line kept from the staged estimate ("${stagedQuote(record)}", estimated at ${at})`
  if (!record.deliveryDate) return `No delivery date kept from the staged estimate (${String(record.noDate?.text ?? 'no date').replace(/[.\s]+$/, '')}, estimated at ${at})`
  return `Delivery date kept from the staged estimate (${record.deliveryDate}, estimated at ${at})`
}

// The delivery-date and ship-date lines, by label (the same labels as the
// server's fillDeliveryEstimateLines(), template-placeholders.js).
const GAP = String.raw`((?:\s|&nbsp;|<[^>]*>|[:\-–—])*?)`
const PLACEHOLDER = String.raw`(INSERT[A-Z0-9]+|X{3,}|DATE)(?![A-Za-z0-9])`
const DELIVERY_LABEL = String.raw`Estimated\s+Delivery(?:\s+Date)?`
const SHIP_LABEL = String.raw`Ship\s+Date|scheduled\s+to\s+ship\s+on`
const escRe = (s) => String(s).replace(/[.*+?^${}()|[\]\\]/g, '\\$&')

// The reply follows the step's estimate (Jenn, 2026-11-03): only the date
// values change, in place; everything else the person wrote stays.
//   A new date: the Estimated Delivery line's value (a placeholder, or a
//   MM/DD/YYYY date) becomes it; the earlier estimate's date wherever else it
//   appears, and a bare INSERTEDD, too. A Ship Date line follows a new ship
//   date the same way.
//   No date but the option's own sentence (dropship, out of stock, the
//   fallback; 2026-11-03): the sentence goes where the date would.
//   Nothing to quote (no option, no mapping, a failed call): the Estimated
//   Delivery value (and the earlier estimate's wherever it appears) becomes
//   INSERTEDD, so Send refuses the reply until the person fills it.
// A reply with no date line and no earlier date is left as it is (no line
// is added). previous: the earlier record (its deliveryDate / shipDate);
// otherDates: more dates to treat as stale (the agent's own estimate).
// { text, changed }.
const DATE_VALUE = String.raw`(\d{2}\/\d{2}\/\d{4}|INSERT[A-Z0-9]+|X{3,}|DATE)(?![A-Za-z0-9/])`
// The label is matched in any case, the value only as written (upper-case
// placeholders): "estimated delivery date" in a sentence is not a line.
const VALUE_CASE_RE = /^(?:\d{2}\/\d{2}\/\d{4}|INSERT[A-Z0-9]+|X{3,}|DATE)$/
export function swapReplyDeliveryDates(text, previous = null, next = null, { otherDates = [] } = {}) {
  let out = String(text ?? '')
  const before = out
  const isHtml = /<[a-z][^>]*>/i.test(out)
  const esc = (v) => (isHtml && v ? String(v).replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;') : v)
  const quote = stagedQuote(next)
  const value = esc(quote) || 'INSERTEDD'
  const shipValue = quote ? esc(next?.shipText ?? next?.shipDate ?? null) : null
  const swap = (oldValue, newValue) => {
    if (!oldValue || !newValue || oldValue === newValue) return
    out = out.replace(new RegExp(`(?<![0-9/])${escRe(oldValue)}(?![0-9/])`, 'g'), newValue)
  }
  for (const d of [esc(stagedQuote(previous)), previous?.deliveryDate, ...(otherDates ?? [])]) swap(d, value)
  out = out.replace(new RegExp(String.raw`\b(${DELIVERY_LABEL})\b${GAP}${DATE_VALUE}`, 'gi'), (whole, label, gap, v) => (v === value || !VALUE_CASE_RE.test(v) ? whole : `${label}${gap}${value}`))
  if (quote) {
    if (shipValue) {
      swap(esc(previous?.shipText ?? previous?.shipDate ?? null), shipValue)
      out = out.replace(new RegExp(String.raw`\b(${SHIP_LABEL})\b${GAP}${PLACEHOLDER}`, 'gi'), (whole, label, gap, v) => (VALUE_CASE_RE.test(v) ? `${label}${gap}${shipValue}` : whole))
    }
    out = out.replace(/INSERTEDD(?![A-Z0-9])/g, value)
  }
  return { text: out, changed: out !== before }
}

// The step's note after the reply followed a re-estimate.
export function replyDateUpdatedNote(next) {
  if (next?.deliveryDate) return `Delivery date in the reply updated to ${next.deliveryDate}.`
  if (stagedQuote(next)) return 'Delivery line in the reply updated to the storefront\'s message.'
  return 'No delivery date: the reply\'s delivery date is now INSERTEDD. Fill it in before sending.'
}

// Whether the reply still carries the earlier estimate's date.
export function replyHasStagedDate(text, record) {
  return Boolean(record?.deliveryDate && String(text ?? '').includes(record.deliveryDate))
}

// --- The order note's delivery line (Jenn, 2026-11-03) ----------------------
// The replacement step's order note (sent to the OMS with the replacement)
// carries one line with the estimate, visible in the note box before Process
// and always the final estimate at Process:
//   "Estimated delivery: 11/04/2026"
//   "Estimated delivery: not available (out of stock)"
//   "Estimated delivery: not available (ships from the vendor)"
//   "Estimated delivery: not available (fallback: 3-7 business days)"
//   "Estimated delivery: not available (<why>)"  nothing to quote
// Never INSERTEDD. One small helper, applied after the note text is built,
// so it composes with whatever wrote the note.
export const EDD_NOTE_LINE_RE = /^[ \t]*Estimated delivery:[^\n]*$/im

const lowerFirst = (s) => (s && /^[A-Z][a-z]/.test(s) ? s.charAt(0).toLowerCase() + s.slice(1) : s)

// The short reason for a record with no date (never the customer sentence).
export function eddNoteReason(record) {
  if (!record || typeof record !== 'object') return null
  if (record.deliveryDate) return null
  if (stagedQuote(record)) {
    if (record.sentenceKind === 'out_of_stock' || record.outOfStock) return 'out of stock'
    if (record.sentenceKind === 'dropship' || record.containsDropship) return 'ships from the vendor'
    const span = /\b\d+\s*(?:-|–|to)\s*\d+\s+(?:business\s+)?days?\b/i.exec(String(stagedQuote(record)))
    return span ? `fallback: ${span[0]}` : 'fallback message'
  }
  // One level of brackets in the note (2026-11-03): "the estimate call
  // failed (the storefront did not answer)" reads "estimate call failed:
  // the storefront did not answer"; a leading "the" goes.
  let why = String(record.noDate?.text ?? '').replace(/^No delivery date(?: from the storefront)?:?\s*/i, '').replace(/[.\s]+$/, '')
  why = why.replace(/^the\s+/i, '')
  const inner = /^(.*?)\s*\((.*)\)$/.exec(why)
  if (inner) why = `${inner[1]}: ${inner[2].replace(/[()]/g, '')}`
  why = why.replace(/[()]/g, '')
  return lowerFirst(why) || 'no estimate'
}

// The note's line for a record, or null (no record, waiting for items).
export function eddNoteLine(record) {
  if (!isStagedEddRecord(record)) return null
  if (record.deliveryDate) return `Estimated delivery: ${record.deliveryDate}`
  return `Estimated delivery: not available (${eddNoteReason(record)})`
}

// The note with the line: an "Estimated delivery:" line already there is
// replaced in place (the person's other edits stay); with none, the line is
// appended only when add is true (at staging and at Process; a re-estimate
// in the agent app does not re-add a line the person deleted).
export function withEddNoteLine(note, record, { add = true } = {}) {
  const text = String(note ?? '')
  const line = eddNoteLine(record)
  if (!line) return text
  if (EDD_NOTE_LINE_RE.test(text)) return text.replace(EDD_NOTE_LINE_RE, line)
  if (!add) return text
  return text.trim() ? `${text.replace(/\s+$/, '')}\n${line}` : line
}
